import React, { useState, useEffect } from 'react';
import { Settings, Pause, Play, RotateCcw, Square } from 'lucide-react';
import fondo from '../../assets/img/fondo_tictactoe.png';
import X from '../../assets/gato/x.png';
import O from '../../assets/gato/o.png';

type Player = 'X' | 'O' | null;
type GameState = 'playing' | 'paused' | 'stopped';
type Difficulty = 'easy' | 'medium' | 'hard';

interface StyleConfig {
    // boardColor: string;
    // xColor: string;
    // oColor: string;
    boardImage: string;
    xImage: string;
    oImage: string;
}
const defaultStyles: StyleConfig = {
    boardImage: fondo,  // Imagen de placeholder por defecto
    xImage: X,        // Imagen de placeholder por defecto
    oImage: O         // Imagen de placeholder por defecto
};

export const TicTacToe: React.FC<{}> = () => {
    const [board, setBoard] = useState<Player[]>(Array(9).fill(null));
    const [isXNext, setIsXNext] = useState<boolean>(true);
    const [gameState, setGameState] = useState<GameState>('stopped');
    const [difficulty, setDifficulty] = useState<Difficulty>('medium');
    const [winner, setWinner] = useState<Player | 'draw' | null>(null);
    // const [styles, setStyles] = useState<StyleConfig>({
    //     boardColor: 'bg-gray-200',
    //     xColor: 'text-blue-500',
    //     oColor: 'text-red-500'
    // });
    const [styles, setStyles] = useState<StyleConfig>(defaultStyles);

    const calculateWinner = (squares: Player[]): Player | 'draw' | null => {
        const lines = [
            [0, 1, 2], [3, 4, 5], [6, 7, 8], // horizontales
            [0, 3, 6], [1, 4, 7], [2, 5, 8], // verticales
            [0, 4, 8], [2, 4, 6] // diagonales
        ];

        for (let line of lines) {
            const [a, b, c] = line;
            if (squares[a] && squares[a] === squares[b] && squares[a] === squares[c]) {
                return squares[a];
            }
        }

        if (squares.every(square => square !== null)) {
            return 'draw';
        }

        return null;
    };

    const getAIMove = (currentBoard: Player[]): number => {
        const emptySquares = currentBoard
            .map((square, index) => square === null ? index : -1)
            .filter(index => index !== -1);

        if (difficulty === 'easy') {
            return emptySquares[Math.floor(Math.random() * emptySquares.length)];
        }

        // Lógica para dificultad media y difícil
        const winningMove = findWinningMove(currentBoard, 'O');
        if (winningMove !== -1) return winningMove;

        if (difficulty === 'hard') {
            const blockingMove = findWinningMove(currentBoard, 'X');
            if (blockingMove !== -1) return blockingMove;

            // Estrategia para esquinas y centro
            if (currentBoard[4] === null) return 4;
            const corners = [0, 2, 6, 8].filter(i => currentBoard[i] === null);
            if (corners.length > 0) {
                return corners[Math.floor(Math.random() * corners.length)];
            }
        }

        return emptySquares[Math.floor(Math.random() * emptySquares.length)];
    };

    const findWinningMove = (currentBoard: Player[], player: Player): number => {
        for (let i = 0; i < currentBoard.length; i++) {
            if (currentBoard[i] === null) {
                const boardCopy = [...currentBoard];
                boardCopy[i] = player;
                if (calculateWinner(boardCopy) === player) {
                    return i;
                }
            }
        }
        return -1;
    };

    useEffect(() => {
        if (gameState === 'playing' && !isXNext && !winner) {
            const timeout = setTimeout(() => {
                const aiMove = getAIMove([...board]);
                handleSquareClick(aiMove);
            }, 500);

            return () => clearTimeout(timeout);
        }
    }, [board, isXNext, gameState]);

    const handleSquareClick = (index: number) => {
        if (gameState !== 'playing' || board[index] || winner) return;

        const newBoard = [...board];
        newBoard[index] = isXNext ? 'X' : 'O';
        setBoard(newBoard);
        setIsXNext(!isXNext);

        const newWinner = calculateWinner(newBoard);
        if (newWinner) setWinner(newWinner);
    };

    const resetGame = () => {
        setBoard(Array(9).fill(null));
        setIsXNext(true);
        setWinner(null);
        setGameState('stopped');
    };

    const toggleGameState = () => {
        if (gameState === 'stopped') {
            setGameState('playing');
        } else if (gameState === 'playing') {
            setGameState('paused');
        } else {
            setGameState('playing');
        }
    };

    const renderSquare = (index: number) => {
        return (
            <button
                className={`w-20 h-20 border border-blue-400 flex items-center justify-center text-4xl font-bold transition-colors duration-200 hover:bg-gray-300`}
                onClick={() => handleSquareClick(index)}
                disabled={gameState !== 'playing' || Boolean(winner)}
                style={{
                    backgroundImage: `url(${styles.boardImage})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center'
                }}
            >
                {board[index] && (
                    <img 
                    src={board[index] === 'X' ? styles.xImage : styles.oImage} 
                    style={{minHeight:'75px', minWidth:'75px', padding:0}} 
                    />
                )}
                {/* {board[index] === 'X' && <span style={{
                    backgroundImage: `url(${styles.xImage})`, backgroundSize: 'cover',
                    backgroundPosition: 'center'
                }}>X
                </span>}
                {board[index] === 'O' && <span style={{
                    backgroundImage: `url(${styles.oImage})`, backgroundSize: 'cover',
                    backgroundPosition: 'center'
                }}>O</span>} */}
            </button >
        );
    };

    return (
        <div className="flex flex-col items-center gap-6 p-8">
            <div className="flex gap-4 items-center">
                <button
                    onClick={toggleGameState}
                    className="px-2 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 flex items-center gap-1"
                >
                    {gameState === 'playing' ? (
                        <><Pause size={20} /> Pausar</>
                    ) : gameState === 'paused' ? (
                        <><Play size={20} /> Continuar</>
                    ) : (
                        <><Play size={20} /> Iniciar</>
                    )}
                </button>
                <button
                    onClick={resetGame}
                    className="px-2 py-2 bg-gray-500 text-white rounded hover:bg-gray-600 flex items-center gap-1"
                >
                    <RotateCcw size={20} /> Reiniciar
                </button>
            </div>

            <div className="flex gap-4 items-center">
                <select
                    value={difficulty}
                    onChange={(e) => setDifficulty(e.target.value as Difficulty)}
                    className="px-2 py-2 border rounded"
                    disabled={gameState === 'playing'}
                >
                    <option value="easy">Fácil</option>
                    <option value="medium">Medio</option>
                    <option value="hard">Difícil</option>
                </select>

                <div className="text-sm text-gray-600">
                    {gameState === 'playing'
                        ? `Turno de: ${isXNext ? 'X' : 'O'}`
                        : gameState === 'paused'
                            ? 'Juego en pausa'
                            : 'Juego detenido'}
                </div>

                {/* <button
          onClick={() => setStyles({
            ...styles,
            boardColor: ['bg-gray-200', 'bg-blue-100', 'bg-green-100'][
              Math.floor(Math.random() * 3)
            ],
            xColor: ['text-blue-500', 'text-purple-500', 'text-green-500'][
              Math.floor(Math.random() * 3)
            ],
            oColor: ['text-red-500', 'text-orange-500', 'text-pink-500'][
              Math.floor(Math.random() * 3)
            ]
          })}
          className="px-4 py-2 bg-purple-500 text-white rounded hover:bg-purple-600 flex items-center gap-2"
        >
          <Settings size={20} /> Cambiar Estilo
        </button> */}
            </div>

            <div className="grid grid-cols-3 gap-1">
                {Array(9).fill(null).map((_, i) => renderSquare(i))}
            </div>

            {winner && (
                <div className="text-xl font-bold">
                    {winner === 'draw'
                        ? '¡Empate!'
                        : `¡Ganador: ${winner}!`}
                </div>
            )}
        </div>
    );
};

export default TicTacToe;